import {
  AnnounceRecording,
  CallRecordingStatus,
  ConversationTrait,
} from '@api/generated/graphql';
import { PcsConversationType } from './lib/getPcsConversation';

export type CallStateDirection = 'originating' | 'terminating' | '';

export type PhoneConnectionCancelledEvent = {
  sessionSid: string;
  reason: 'timeout' | 'error' | 'user-cancelled';
};

export type WebphoneCallState = {
  connected: boolean;
  callId: string;
  direction: CallStateDirection;
  ended: boolean;
  established: boolean;
  hasSession: boolean;
  held: boolean;
  inTransfer: boolean;
  isAudioMuted: boolean;
  isVideoMuted: boolean;
  localIdentity: string;
  originating: boolean;
  primary: boolean;
  progress: boolean;
  remoteIdentity: string;
  terminating: boolean;
  registrationFailed: boolean;
  internalCall: boolean;
  remoteCallId: string;
  lastTriggeringEvent: string;
};

enum ExtendedRecordingStatus {
  Unknown = 'Unknown',
}

export const RecordingStatus = {
  ...CallRecordingStatus,
  ...ExtendedRecordingStatus,
};

export type CallRecordingStatusExtended =
  | CallRecordingStatus
  | ExtendedRecordingStatus;

export type RecordingState = {
  status: CallRecordingStatus | CallRecordingStatusExtended;
  disabled: boolean;
  announceRecording: AnnounceRecording;
};

export type CallAttributes = {
  pcsConversationSid: string;
  queueIds: string[];
  recordingState: RecordingState;
  tenantId: string;
  traits: ConversationTrait[];
};

export type CallEvent = WebphoneCallState & CallAttributes;

export type PcsConversationFetchedEvent = {
  pcsConversation: PcsConversationType;
  phoneStateUpdateEvent: WebphonePhoneStateEvent;
};

export type DialerState = {
  actionDisplay: string;
  actionColor: string;
  dialedNumbers: string;
};

export type PhoneState = {
  phoneConnected: boolean;
  connectionStatus: WebphoneConnectionStatus;
  registrationFailed: boolean;
  callList: WebphoneCallState[];
  callAttributes: {
    [callId: string]: CallAttributes;
  };
  registrationOverwritten: boolean;
  callsAccepting: string[];
  disconnectTriggeredLocally: boolean;
  debugInfo: {
    [key: string]: any;
  };
  existingRegistrationsDetected: boolean;
  isUnactivatedImpersonator: boolean;
};

export type SoftPhoneCallControl = {
  answer: (callId?: string) => void;
  cancel: (callId: string) => void;
  hangup: (callId?: string) => void;
  hold: () => void;
  mute: () => void;
  unhold: (callId?: string) => void;
  unmute: () => void;
  transfer: (number: string) => void;
};

export type SoftPhoneDialer = {
  dial: (number: string | number) => void;
  clear: () => void;
  backspace: () => void;
  autoAction: () => void;
};

export type PhonePrepareOutgoingEvent = {
  outgoingNumber: string;
  displayName?: string;
  tenantId?: string;
  taskAttributes?: Record<string, any>;
};

export type WebphonePhoneStateEvent = {
  triggeringEvent: string;
  phoneConnected: boolean;
  connectionStatus: WebphoneConnectionStatus;
  registrationFailed: boolean;
  callState: WebphoneCallState;
  callList: WebphoneCallState[];
};

export type WebphoneCallEvent = {
  callId: string;
  cause: string;
  phoneNumber: string;
  reasonPhrase: string;
  eventType: string;
  originator: string;
  sipResponse: {
    reasonPhrase: string;
    statusCode: number;
  };
};

export type WebphoneCallEventPayloadEventDetails = {
  callId: string;
  phoneNumber: string;
  reasonPhrase: string;
  cause: string;
  originator: string;
  sipResponse: {
    reasonPhrase: string;
    statusCode: number;
  };
};

export type WebphoneCallEventPayload = {
  event: WebphoneCallEventPayloadEventDetails;
  eventType: string;
};

export type WebphoneConnectionInfoEvent = {
  connectionDetails: {
    userAgentConnected: boolean;
    userAgentIsReady: boolean;
    userAgentResgistered: boolean;
    userAgentStarted: boolean;
    webphoneConnected: boolean;
    webphoneRegistrationReportedFailed: boolean;
    websocketOptionsPingLatencyMs: number;
    websocketOptionsPingStatus: string;
  };
  connectionStatus: string;
};

export type SoftPhoneFeatureToggles = {
  enableCallMonitoring: boolean;
  preventDuplicateWebphoneRegistrations: boolean;
};

export type WebphoneUserContext = {
  pcsUserId: string;
  pcsAccountId: string;
  pcsContactCenterId: string;
};

/**
 * Webphone-specific configuration passed to the MFE
 */
export type WebphoneConfig = {
  jwt?: string; // Legacy: SIP JWT (when feature-rtc-webphone-handles-fetching-jwt is disabled)
  sipUri?: string; // Legacy: SIP URI (when feature-rtc-webphone-handles-fetching-jwt is disabled)
  hostHandlesWindowUnload?: boolean;
  debugLogging: boolean;
  pcsJwt: string;
  pcsApiUrl: string | undefined;
};

export type OutboundCallerIdInput = {
  name: string;
  number: string;
  tenantId: string | null;
};

export type RegistrationInfoEvent = {
  collectionTime: string;
  totalRegistrations: number;
  webphoneRegistrations: number;
  registrationTypesPresent: string[];
  registrations: SoftphoneRegistration[];
};

type SoftphoneRegistration = {
  authorizingId: string;
  authorizingType: string;
  username: string;
  userAgent: string;
  userId: string;
  firstRegistration: boolean;
  initialRegistration: string;
  lastRegistration: string;
  name: string;
  enabled: boolean;
  deviceType: string;
  ownerId: string;
};

export enum WebphoneConnectionStatus {
  Uninitialized = 'uninitialized',
  WaitingToConnect = 'waitingToConnect',
  Connecting = 'connecting',
  Connected = 'connected',
  Disconnected = 'disconnected',
  RegistrationFailed = 'registrationFailed',
  RegistrationOverwritten = 'registrationOverwritten',
  UserAgentNotStarted = 'userAgentNotStarted',
  UserAgentNotConnected = 'userAgentNotConnected',
  UserAgentNotRegistered = 'userAgentNotRegistered',
  UserAgentNotReady = 'userAgentNotReady',
  WebsocketOptionsPingWaiting = 'websocketOptionsPingWaiting',
  WebsocketOptionsPingFailed = 'websocketOptionsPingFailed',
  WebsocketOptionsPingTimeout = 'websocketOptionsPingTimeout',
  WebphoneJwtFetching = 'webphoneJwtFetching',
  WebphoneJwtFetchFailed = 'webphoneJwtFetchFailed',
  WebphoneJwtNotAvailable = 'webphoneJwtNotAvailable',
}

export enum WebphoneUnloadWarningOptions {
  Always = 'Always',
  Never = 'Never',
  OnActiveCallOnly = 'OnActiveCallOnly',
}
